import React, { useEffect, useMemo, useState } from "react";
import { Typography, Alert, Spin } from "antd";
import RegistrationBox from "../RegistrationBox";
import { FormFields, useRegistration } from "../../../contexts/RegistrationContext";
import { getAgentPackages } from "../../../services/fhpService";
import {
  buildCatalogFromPackagesResponse,
  resolveBillingPackageId,
} from "../../../utils/agentRegistrationPackageId";
import { getRegistrationPlanBillingSummary } from "../../../utils/agentRegistrationPlans";

const { Title, Paragraph, Text } = Typography;

/**
 * Summary for billing step: שם + תווית מחיר מתוך תוכנית הרישום הסטטית;
 * packageId לדראפט נגזר מאותו קטלוג כמו בשלב הקודם.
 */
export function useSelectedPackageSummary(packageCode) {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getAgentPackages();
        const raw = response?.data?.packages;
        if (Array.isArray(raw) && raw.length) {
          const cat = buildCatalogFromPackagesResponse(raw);
          if (!cancelled) setCatalog(cat);
        }
      } catch {
        if (!cancelled) setCatalog([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const summary = useMemo(() => {
    const { name, monthlyPriceLabel } = getRegistrationPlanBillingSummary(packageCode);
    return { name, monthlyPriceLabel };
  }, [packageCode]);

  const { packageId: numericPackageId, source: packageIdSource } = useMemo(
    () => resolveBillingPackageId(packageCode, catalog),
    [packageCode, catalog],
  );

  return { loading, ...summary, numericPackageId, packageIdSource };
}

const BillingActivationStage = () => {
  const { formData } = useRegistration();
  const pkgCode = formData[FormFields.ADDITIONAL_INFO.PACKAGE];
  const { loading, name, monthlyPriceLabel, packageIdSource } = useSelectedPackageSummary(pkgCode);

  const agencyUsesProBilling =
    String(pkgCode || "").trim().toUpperCase() === "AGENCY" &&
    packageIdSource != null &&
    String(packageIdSource).startsWith("TODO_AGENCY_UNMAPPED");

  return (
    <RegistrationBox title="הפעלת חשבון · אמצעי תשלום">
      {loading ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Title level={4} style={{ marginTop: 0 }}>
            {name}
          </Title>
          <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>מחיר חודשי: </Text>
            <Text>{monthlyPriceLabel}</Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 8 }} type="secondary">
            <Text>ללא התחייבות · המחיר כולל מע״מ</Text>
          </Paragraph>
          {agencyUsesProBilling ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="מיפוי חבילת סוכנות בשרת"
              description="חבילת הסוכנות נבחרה באופן מלא בממשק. עד לפרסום מזהה חיוב ייעודי בקטלוג השרת, פרטי המנוי בדף התשלום עשויים להתיישר עם מזהה ה-PRO הטכני — יש לאמת מול הצוות כשמופיע מיפוי AGENCY בקטלוג."
            />
          ) : null}
          <Alert
            type="info"
            showIcon
            message="לא יתבצע חיוב כעת"
            style={{ marginBottom: 12 }}
          />
          <Alert
            type="info"
            showIcon
            message="30 ימי ניסיון יחלו רק לאחר אישור החשבון"
            style={{ marginBottom: 12 }}
          />
          <Alert
            type="info"
            showIcon
            message="החשבון יופעל רק לאחר אישור ידני"
            description="תועברו לדף תשלום מאובטח של Hyp לשמירת אמצעי תשלום בלבד. לא מזינים כרטיס באתר HotelsRiver ולא נשמרים פרטי כרטיס אצלנו."
          />
        </>
      )}
    </RegistrationBox>
  );
};

export default BillingActivationStage;
